import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from './Sidebar';
import HorizontalStepper from './HorizontalStepper';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Heading, Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import PersonalInfo from './PersonalInfo';
import Summary from './Summary';
import WorkExperience from './WorkExperience';
import Education from './Education';
import Language from './Language';
import Certificates from './Certificates';
import Projects from './Projects';
import Skills from './Skills';
import ProfilePicture from './ProfilePicture';
import ActionButtons from './ActionButtons';
import Achievements from './Achievements';
import Navbar from '../../../Company/About/Navbar';
import VideoPreviewArea from '../createVideo/VideoPreviewArea';
import VideoGuide from '../createVideo/VideoGuide';
import VideoAnalysisFeedback from '../createVideo/VideoAnalysisFeedback';
import { getUploadConfig, uploadVideoDirect, uploadVideoToServer } from '../createVideo/videoUpload';
import { toast } from 'sonner';

function CreateResume() {
  const [loading, setLoading] = useState(true);
  const [resumeData, setResumeData] = useState({
    personalInfo: {},
    summary: '',
    workExperience: [],
    education: [],
    languages: [],
    certificates: [],
    projects: [],
    skills: [],
    achievements: [],
    profilePhoto: null,
  });

  const [validationErrors, setValidationErrors] = useState([]);
  const [activeSection, setActiveSection] = useState('Personal Info');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [currentView, setCurrentView] = useState('resume'); // 'resume' or 'video'
  const [resumeCompleted, setResumeCompleted] = useState(false);
  const [videoCompleted, setVideoCompleted] = useState(false);

  // Video recording states
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideoURL, setRecordedVideoURL] = useState(null);
  const [existingVideoURL, setExistingVideoURL] = useState(null);
  const [videoMode, setVideoMode] = useState('existing'); // 'existing', 'create', 'upload'
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Recording duration bookkeeping — passed to the AI analysis so words/min
  // is computed from the real length, and used for the 3-minute auto-stop.
  const recordStartTsRef = useRef(null);
  const maxDurationTimerRef = useRef(null);
  const [recordedDuration, setRecordedDuration] = useState(null);
  const MAX_RECORD_SECONDS = 180;
  // Single-upload review coordination (see captureReviewBaseline below).
  const [justSaved, setJustSaved] = useState(false);
  const reviewBaselineRef = useRef(null);

  // Create refs for each section using useRef - stable across renders
  const sectionRefs = useRef({
    'Personal Info': React.createRef(),
    'Summary': React.createRef(),
    'Work Experience': React.createRef(),
    'Education': React.createRef(),
    'Language': React.createRef(),
    'Certification': React.createRef(),
    'Project': React.createRef(),
    'Skills': React.createRef(),
    'Achievements': React.createRef(),
  });

  // Handle section click - scroll to section
  const handleSectionClick = useCallback((sectionName) => {
    setActiveSection(sectionName);
    const ref = sectionRefs.current[sectionName];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      // Add a small offset for better visibility
      setTimeout(() => {
        window.scrollBy(0, -100);
      }, 300);
    }
  }, []);

  // Add validation function
  const validateForm = () => {
    const errors = [];
    
    // Personal Info validation
    const personalInfo = resumeData.personalInfo;
    if (!personalInfo.firstName?.trim()) errors.push("First Name");
    if (!personalInfo.lastName?.trim()) errors.push("Last Name");
    if (!personalInfo.phoneNumber?.trim()) errors.push("Phone Number");
    if (!personalInfo.currentCtc) errors.push("Current CTC");
    if (!personalInfo.expectedCtc) errors.push("Expected CTC");
    if (!personalInfo.noticePeriod) errors.push("Notice Period");
    if (!personalInfo.currentLocation?.trim()) errors.push("Current Location");
    if (!personalInfo.currentOrganization?.trim()) errors.push("Current Organization");
    
    // Profile Photo validation
    if (!resumeData.profilePhoto) errors.push("Profile Photo");
    
    // Other mandatory sections
    if (!resumeData.summary?.trim()) errors.push("Professional Summary");
    if (!resumeData.workExperience?.length) errors.push("Work Experience");
    if (!resumeData.education?.length) errors.push("Education");
    if (!resumeData.skills?.length) errors.push("Skills");
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Fetch previously stored resume data when the component mounts.
  useEffect(() => {
    async function fetchResumeData() {
      try {
        const response = await fetch('/api/get_resume');
        if (response.ok) {
          const data = await response.json();
          setResumeData(data);
          console.log('Data: ', data);

          if (data.prefilledFromSignupResume) {
            toast.success('We prefilled your resume from the CV you uploaded at signup', {
              description: 'Please review each section, fix anything that looks off, and save.',
              duration: 8000,
            });
          }
          
          // Check if resume is complete (basic validation)
          if (data.personalInfo?.firstName && data.profilePhoto && data.summary && 
              data.workExperience?.length > 0 && data.education?.length > 0 && 
              data.skills?.length > 0) {
            setResumeCompleted(true);
          }
          
          // Track completed sections
          const completed = [];
          if (data.personalInfo?.firstName && data.profilePhoto) completed.push('Personal Info');
          if (data.summary?.trim()) completed.push('Summary');
          if (data.workExperience?.length > 0) completed.push('Work Experience');
          if (data.education?.length > 0) completed.push('Education');
          if (data.skills?.length > 0) completed.push('Skills');
          if (data.languages?.length > 0) completed.push('Language');
          if (data.certificates?.length > 0) completed.push('Certification');
          if (data.projects?.length > 0) completed.push('Project');
          if (data.achievements?.length > 0) completed.push('Achievements');
          setCompletedSections(completed);
          
          setLoading(false);
        } else {
          console.error('Error fetching resume data:', response.statusText);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    }
    
    async function checkVideoExists() {
      try {
        const response = await fetch('/api/check-video');
        if (response.ok) {
          const data = await response.json();
          if (data.hasVideo && data.videoUrl) {
            setVideoCompleted(true);
            setExistingVideoURL(data.videoUrl);
            setVideoMode('existing');
          } else {
            setVideoMode('create');
          }
        }
      } catch (error) {
        console.error('Error checking video:', error);
        setVideoMode('create');
      }
    }
    
    fetchResumeData();
    checkVideoExists();
  }, []);

  // Track completed sections based on resumeData
  useEffect(() => {
    const completed = [];
    const { personalInfo, profilePhoto, summary, workExperience, education, skills, languages, certificates, projects, achievements } = resumeData;
    
    if (personalInfo?.firstName && profilePhoto) completed.push('Personal Info');
    if (summary?.trim()) completed.push('Summary');
    if (workExperience?.length > 0) completed.push('Work Experience');
    if (education?.length > 0) completed.push('Education');
    if (skills?.length > 0) completed.push('Skills');
    if (languages?.length > 0) completed.push('Language');
    if (certificates?.length > 0) completed.push('Certification');
    if (projects?.length > 0) completed.push('Project');
    if (achievements?.length > 0) completed.push('Achievements');
    
    setCompletedSections(completed);
  }, [resumeData]);

  // Callback functions to update state from each module - wrapped in useCallback to prevent re-renders
  const updatePersonalInfo = useCallback((data) => {
    setResumeData((prev) => ({ ...prev, personalInfo: data }));
  }, []);

  const updateSummary = useCallback((data) => {
    setResumeData((prev) => ({ ...prev, summary: data }));
  }, []);

  const updateWorkExperience = useCallback((data) => {
    setResumeData((prev) => ({ ...prev, workExperience: data }));
  }, []);

  const updateEducation = useCallback((data) => {
    setResumeData((prev) => ({ ...prev, education: data }));
  }, []);

  const updateLanguage = useCallback((data) => {
    setResumeData((prev) => ({ ...prev, languages: data }));
  }, []);

  const updateCertificates = useCallback((data) => {
    setResumeData((prev) => ({ ...prev, certificates: data }));
  }, []);

  const updateProjects = useCallback((data) => {
    setResumeData((prev) => ({ ...prev, projects: data }));
  }, []);

  const updateSkills = useCallback((data) => {
    setResumeData((prev) => ({ ...prev, skills: data }));
  }, []);

  const updateAchievements = useCallback((data) => {
    setResumeData((prev) => ({ ...prev, achievements: data }));
  }, []);

  // Function to handle profile photo upload to S3
const handleProfilePhotoUpload = async (file) => {
  try {
    const formData = new FormData();
    formData.append('profilePhoto', file);

    const response = await fetch('/api/upload_profile_picture', {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      const result = await response.json();
      // Update the resumeData with the new URL
      setResumeData((prev) => ({
        ...prev,
        profilePhoto: result.url
      }));
      console.log('Profile picture uploaded successfully:', result.url);
      return Promise.resolve(); // Resolve the promise on success
    } else {
      const error = await response.json();
      console.error('Error uploading profile picture:', error);
      return Promise.reject(error); // Reject the promise on error
    }
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    return Promise.reject(error); // Reject the promise on network error
  }
};

  // Video recording functions
  useEffect(() => {
    if (isCameraOn && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isCameraOn]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      setIsCameraOn(true);
      setRecordedVideoURL(null);
    } catch (error) {
      console.error("Error accessing camera:", error);
      alert("Unable to access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraOn(false);
  };

  const startRecording = () => {
    if (streamRef.current) {
      const options = { mimeType: "video/webm; codecs=vp8" };
      const mediaRecorder = new MediaRecorder(streamRef.current, options);
      mediaRecorderRef.current = mediaRecorder;
      let chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (recordStartTsRef.current) {
          setRecordedDuration(
            Math.round((Date.now() - recordStartTsRef.current) / 1000)
          );
          recordStartTsRef.current = null;
        }
        if (maxDurationTimerRef.current) {
          clearTimeout(maxDurationTimerRef.current);
          maxDurationTimerRef.current = null;
        }
        if (chunks.length > 0) {
          const blob = new Blob(chunks, { type: "video/webm" });
          if (blob.size > 0) {
            const url = URL.createObjectURL(blob);
            setRecordedVideoURL(prev => {
              if (prev) URL.revokeObjectURL(prev);
              return url;
            });
            stopCamera();
          }
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      recordStartTsRef.current = Date.now();
      setRecordedDuration(null);
      // Hard cap: auto-stop at 3 minutes so nobody uploads a 20-minute file.
      maxDurationTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
          mediaRecorderRef.current.stop();
          setIsRecording(false);
        }
      }, MAX_RECORD_SECONDS * 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      setTimeout(() => {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }, 500);
    }
  };

  const startOver = () => {
    setRecordedVideoURL(null);
    setRecordedDuration(null);
    setIsCameraOn(false);
    startCamera();
  };

  // Function to save data to backend
  const handleFinalSave = async () => {
    if (!validateForm()) {
      alert('Please complete all mandatory fields before saving.');
      return;
    }
    try {
      const response = await fetch('/api/save_resume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resumeData),
      });

      const result = await response.json();
      if (response.ok) {
        console.log('Resume data saved successfully:', result);
        setResumeCompleted(true);
        setShowSuccessModal(true);
        
        // After saving, switch to video resume section if no video exists
        setTimeout(() => {
          if (!videoCompleted) {
            setCurrentView('video');
            // Scroll to top smoothly
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 1500);
      } else {
        console.error('Error saving resume data:', result);
        alert('Failed to save resume data');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to save resume data');
    }
  };

  // Handle view switching from action buttons
  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  // Handle video mode change
  const handleVideoModeChange = (mode) => {
    setVideoMode(mode);
    if (mode === 'create') {
      setRecordedVideoURL(null);
      setIsCameraOn(false);
    }
  };

  // Snapshot the current review timestamp BEFORE a save so the review panel can
  // tell a freshly-generated review apart from an older one. The save endpoint
  // generates the review async; the panel polls for it (no second upload).
  const captureReviewBaseline = async () => {
    try {
      const r = await fetch('/api/video-resume/analysis', { credentials: 'include' });
      const j = await r.json();
      reviewBaselineRef.current = j?.analysis?.analyzed_at || null;
    } catch { reviewBaselineRef.current = null; }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('Please select a valid video file');
      event.target.value = ''; // Reset file input
      return;
    }

    // Size cap depends on the active upload path (direct-to-S3 allows much more).
    const cfg = await getUploadConfig();
    const maxSize = cfg.directUpload ? cfg.maxDirectBytes : cfg.maxServerBytes;
    if (file.size > maxSize) {
      alert(`Video is too large — maximum ${Math.round(maxSize / 1024 / 1024)} MB. Record a shorter clip or lower your camera quality.`);
      event.target.value = ''; // Reset file input
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    await captureReviewBaseline();

    try {
      // Real upload progress; direct-to-S3 when enabled (handles big iPhone
      // videos), else legacy server upload. Helpers throw on failure → caught below.
      if (cfg.directUpload) {
        await uploadVideoDirect({
          endpoints: { presign: '/api/video-resume/presign', finalize: '/api/video-resume/finalize' },
          file, durationSeconds: null, onProgress: setUploadProgress,
        });
      } else {
        await uploadVideoToServer({
          url: '/api/upload-video', file, durationSeconds: null, onProgress: setUploadProgress,
        });
      }
      setUploadProgress(100);

      setVideoCompleted(true);
      setJustSaved(true);
      // Refresh the URL, then show it + the async AI review in the existing view.
      const checkResponse = await fetch('/api/check-video');
      if (checkResponse.ok) {
        const data = await checkResponse.json();
        if (data.hasVideo && data.videoUrl) {
          setExistingVideoURL(data.videoUrl);
          setTimeout(() => {
            setVideoMode('existing');
            setIsUploading(false);
            setUploadProgress(0);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      setUploadProgress(0);
      alert(error.message || 'Upload failed. Please try again.');
    }
    
    // Reset file input
    event.target.value = '';
  };


  if (loading) {
    return (
      <>
        <Navbar />
        <div className="bg-white overflow-hidden px-7 py-10">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="flex gap-4">
                {/* Sidebar Skeleton */}
                <div className="w-[204px] bg-white rounded-2xl shadow-md p-11">
                  <div className="h-6 bg-muted rounded mb-6"></div>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                    <div key={item} className="h-12 bg-muted rounded mb-3.5"></div>
                  ))}
                </div>
                
                {/* Form Content Skeleton */}
                <div className="flex-1">
                  {[1, 2, 3].map((section) => (
                    <div key={section} className="bg-white border border-primary rounded-2xl p-7 mb-8">
                      <div className="h-6 bg-muted rounded mb-6"></div>
                      <div className="grid grid-cols-2 gap-5">
                        <div className="h-12 bg-muted rounded"></div>
                        <div className="h-12 bg-muted rounded"></div>
                        <div className="h-12 bg-muted rounded"></div>
                        <div className="h-12 bg-muted rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Side Column Skeleton */}
            <div className="w-1/5">
              <div className="bg-white rounded-2xl shadow-md p-6">
                <div className="w-32 h-32 bg-muted rounded-full mx-auto mb-4"></div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="bg-white overflow-hidden">
        {/* Horizontal Stepper Navigation */}
        <HorizontalStepper
          activeSection={activeSection}
          completedSections={completedSections}
          onSectionClick={handleSectionClick}
        />
        
        <div className="px-4 py-6 max-w-5xl mx-auto">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="flex-1">
                {currentView === 'resume' ? (
                  <>
                    <div ref={sectionRefs.current['Personal Info']}>
                      <PersonalInfo onUpdate={updatePersonalInfo} data={resumeData.personalInfo} />
                    </div>
                    <div ref={sectionRefs.current['Summary']}>
                      <Summary onUpdate={updateSummary} data={resumeData.summary} />
                    </div>
                    <div ref={sectionRefs.current['Work Experience']}>
                      <WorkExperience onUpdate={updateWorkExperience} data={resumeData.workExperience} />
                    </div>
                    <div ref={sectionRefs.current['Education']}>
                      <Education onUpdate={updateEducation} existingEducation={resumeData.education} />
                    </div>
                    <div ref={sectionRefs.current['Language']}>
                      <Language onUpdate={updateLanguage} existingLanguages={resumeData.languages} />
                    </div>
                    <div ref={sectionRefs.current['Certification']}>
                      <Certificates onUpdate={updateCertificates} existingCertificates={resumeData.certificates} />
                    </div>
                    <div ref={sectionRefs.current['Project']}>
                      <Projects onUpdate={updateProjects} existingProjects={resumeData.projects} />
                    </div>
                    <div ref={sectionRefs.current['Skills']}>
                      <Skills onUpdate={updateSkills} existingSkills={resumeData.skills} />
                    </div>
                    <div ref={sectionRefs.current['Achievements']}>
                      <Achievements onUpdate={updateAchievements} existingAchievements={resumeData.achievements} />
                    </div>
                  </>
                ) : (
                  <div className="w-full">
                    <Heading level="h2" className="text-2xl font-semibold mb-6">Video Resume</Heading>
                    
                    {/* Mode Selection Buttons */}
                    <div className="flex gap-3 mb-6">
                      {existingVideoURL && (
                        <Button 
                          variant={videoMode === 'existing' ? 'default' : 'outline'}
                          onClick={() => handleVideoModeChange('existing')}
                        >
                          View Existing Video
                        </Button>
                      )}
                      <Button 
                        variant={videoMode === 'create' ? 'default' : 'outline'}
                        onClick={() => handleVideoModeChange('create')}
                      >
                        Create New Video
                      </Button>
                      <Button 
                        variant={videoMode === 'upload' ? 'default' : 'outline'}
                        onClick={() => {
                          setVideoMode('upload');
                          fileInputRef.current?.click();
                        }}
                      >
                        Upload Video
                      </Button>
                    </div>

                    {/* Hidden file input for upload */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/*"
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                    />

                    {/* Existing Video Display */}
                    {videoMode === 'existing' && existingVideoURL && (
                      <div className="w-full mb-6">
                        <div className="bg-green-100 text-green-800 px-4 py-2 rounded-md mb-4 flex items-center gap-2">
                          <span>✓</span> Video Resume Uploaded
                        </div>
                        <video 
                          src={existingVideoURL} 
                          controls 
                          className="w-full rounded-lg"
                        />
                        <Text className="mt-4 text-muted-foreground">
                          This is your current video resume. You can create a new one or upload a different video to replace it.
                        </Text>
                        {/* AI review — polled from the async review the save
                            kicked off (single upload, no re-analyze round-trip). */}
                        <div className="mt-4">
                          <VideoAnalysisFeedback
                            active
                            justSaved={justSaved}
                            baselineAnalyzedAt={reviewBaselineRef.current}
                          />
                        </div>
                      </div>
                    )}

                    {/* Create New Video — camera left, AI guide/teleprompter right */}
                    {videoMode === 'create' && !isUploading && (
                      <div className="w-full">
                        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
                          <div className="w-full space-y-4">
                            <VideoPreviewArea
                              videoRef={videoRef}
                              isCameraOn={isCameraOn}
                              isRecording={isRecording}
                              recordedVideoURL={recordedVideoURL}
                              startCamera={startCamera}
                              stopCamera={stopCamera}
                              startRecording={startRecording}
                              stopRecording={stopRecording}
                              startOver={startOver}
                              onSaveStart={captureReviewBaseline}
                              onVideoSaved={async () => {
                                setVideoCompleted(true);
                                setJustSaved(true);
                                // Refresh the URL, then show the saved video +
                                // its async AI review in the existing view.
                                const checkResponse = await fetch('/api/check-video');
                                if (checkResponse.ok) {
                                  const data = await checkResponse.json();
                                  if (data.hasVideo && data.videoUrl) {
                                    setExistingVideoURL(data.videoUrl);
                                    setVideoMode('existing');
                                  }
                                }
                              }}
                            />
                          </div>
                          <VideoGuide
                            resumeData={resumeData}
                            onVideoArrived={(data) => {
                              // Phone upload landed — same refresh as a
                              // desktop save.
                              setVideoCompleted(true);
                              if (data?.videoUrl) {
                                setExistingVideoURL(data.videoUrl);
                                setVideoMode('existing');
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Upload Progress Indicator */}
                    {isUploading && (
                      <div className="flex items-center justify-center min-h-[400px]">
                        <div className="text-center">
                          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                          <Heading level="h3" className="text-xl font-semibold mb-4">Uploading Video...</Heading>
                          <div className="w-64 h-2 bg-muted rounded-full overflow-hidden mb-2">
                            <div 
                              className="h-full bg-primary transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                          <Text>{uploadProgress}% Complete</Text>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/5 flex flex-col items-center gap-4">
              <ProfilePicture 
                onUpdate={handleProfilePhotoUpload} 
                profilePhoto={resumeData.profilePhoto} 
              />
              <ActionButtons 
                onViewChange={handleViewChange}
                currentView={currentView}
                resumeCompleted={resumeCompleted}
                videoCompleted={videoCompleted}
              />
            </div>
          </div>
        </div>
        {currentView === 'resume' && (
          <div className="flex justify-center py-8 mt-5">
            <Button onClick={handleFinalSave} className="min-w-[200px]">
              Save All
            </Button>
          </div>
        )}
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent>
          <DialogHeader>
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <svg 
                  width="32" 
                  height="32" 
                  viewBox="0 0 64 64" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M20 32L28 40L44 24" 
                    stroke="white" 
                    strokeWidth="4" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <DialogTitle>Resume Saved Successfully! ✓</DialogTitle>
              <DialogDescription>
                {!videoCompleted ? (
                  <Text>Great! Now let's create your video resume to complete your profile.</Text>
                ) : (
                  <Text>Your resume has been saved successfully.</Text>
                )}
              </DialogDescription>
              <Button 
                onClick={() => {
                  setShowSuccessModal(false);
                  if (!videoCompleted) {
                    setCurrentView('video');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                {!videoCompleted ? 'Create Video Resume →' : 'OK'}
              </Button>
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CreateResume;